import { useFeatureFlagEnabled } from '@/app-shell/flags/posthog-compat';
import { isTauri } from '@/tauri/utils';
import { useEffect } from 'react';

import { t2medBridge } from '../api/bridge';
import { useT2medAuthBridge } from '../hooks/useT2medAuthBridge';
import { useT2medLaunchEvents } from '../hooks/useT2medLaunchEvents';
import { useT2medRouteSessionActivity } from '../hooks/useT2medRouteSessionActivity';
import { T2MED_FEATURE_FLAG } from '../types';

/**
 * Inner component so the hooks only mount when T2med is enabled.
 */
function T2medBridgeRunner() {
  useT2medAuthBridge();
  useT2medLaunchEvents();
  useT2medRouteSessionActivity();

  // Runtime kill switch: when the flag flips OFF this runner unmounts, so
  // drop the plugin's cloud auth (and with it all T2med launch state) —
  // otherwise its writeback poller would keep running until next restart.
  // The auth bridge re-pushes the token if the runner remounts.
  useEffect(() => {
    return () => {
      void t2medBridge.clearCloudAuthToken();
    };
  }, []);

  return null;
}

/**
 * Hidden app-level component that activates the T2med desktop bridge:
 * pushes cloud auth into `tauri-plugin-t2med`, reacts to launch events
 * (navigation + toasts), and syncs session activity for the writeback
 * poll cadence.
 *
 * Mount once at the app level (AuthWrapper), like `<GdtLauncher />`.
 * Renders nothing.
 *
 * Guards:
 * - Only active in Tauri desktop builds.
 * - Only active when `release-scribe-enable-t2med-integration` is on —
 *   the same key gating the backend endpoints, so flag-off is a full
 *   kill switch across the stack. With the flag off the plugin stays
 *   inert: no auth is pushed on start, and a runtime flip-off clears
 *   any auth already pushed, so its poller has no working cloud client.
 */
export function T2medLauncher() {
  const isT2medEnabled = useFeatureFlagEnabled(T2MED_FEATURE_FLAG);

  // useFeatureFlagEnabled returns undefined while PostHog loads — treat
  // as disabled; the component re-renders when the flag resolves.
  if (!isTauri() || !isT2medEnabled) {
    return null;
  }

  return <T2medBridgeRunner />;
}
